import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    validateDetailedBookingIMPFields
} from '../../QueueSheetManagement/utils/validateBookingFieldsHelpers/validateDetailedBookingIMPFields';
import {
    DETAILED_BOOKING_IMP_FIELDS, IMPFormErrorType
} from '../constants/DetailedBookingConstants';
import { computeExistingContextualNodeValues } from '../services/bookingApprovalUtils';
import { FORM_FIELDS } from '../constants/FormFieldsConstants';
import {
    CreateDetailedBookingIMPFormProps, DetailedBookingIMPData, DetailedBookingIMPMainFormFieldErrors, DetailedBookingIMPMainFormFields,
    IMPContextualNodeData, IMPContextualNodeErrors,
    PreviousNodesValues
} from '../interface/DetailedBookingInterface';
import { Option } from '../interface/FormFieldInterfaces';
import { getProcessedValue } from '../services/helpers';
import { handleContextualNodeChange } from '../services/impFormHelpers/handleContextualNodeChange';
import { useGetArtEvents } from '../../../../EditArtEvent/hooks/useGetArtEvents';
import { useAppContext } from '../../../../Shared/store/appContext';

export const useCreateDetailedBookingIMPForm = ({
    initialData,
    bookingId,
    entryId,
    onFormSubmit,
    validateFormTrigger,
    existingContextualNodes,
    resetDetailedFormErrors,
    setResetDetailedFormErrors,
    filteredSlotPackages,
    isEditMode,
    hideCommonFields,
    onSlotSelectionChange,
    preSelectedSlotNames,
    budgetUsedByOtherSlots = 0,
    onSlotBudgetChange,
}: CreateDetailedBookingIMPFormProps) => {
    const { t } = useTranslation();

    // Resolve whether the selected Art Event is actually IN waitlist mode — i.e. it has opted in
    // (waitlistEnabled === true), which is exactly what the backend checks to decide whether an
    // over-request is waitlisted vs rejected. This is distinct from waitlistFeatureEnabled (the Weblab
    // flag that only governs whether an event *may* opt in): a feature-available but not-opted-in event
    // must NOT show the waitlist over-request affordances. useGetArtEvents shares its React Query cache.
    const { state } = useAppContext();
    const { data: artEventsData, isLoading: isArtEventsLoading } = useGetArtEvents();
    const selectedArtEvent = artEventsData?.artEvents?.find(
        (event) => event.artEventID === state.selectedArtEventId
    );
    // While the Art Event list is still loading we don't yet know the waitlist state. Bias permissive in that
    // window (when an event is selected) so a valid waitlist over-request isn't wrongly blocked on first
    // render; the backend remains the real enforcement. Once loaded, use the actual value.
    const isWaitlistEnabled = selectedArtEvent
        ? selectedArtEvent.waitlistEnabled === true
        : isArtEventsLoading && !!state.selectedArtEventId;

    // Use passed filtered slot packages instead of fetching
    const slotPackagesData = filteredSlotPackages || [];

    const effectiveRoValue = Math.max(0, (initialData.exactBillingValue || 0) - budgetUsedByOtherSlots);

    const [formData, setFormData] = useState<DetailedBookingIMPData>({
        entryNumber: entryId ?? 0,
        bookingId: bookingId ?? "",
        brand: initialData.brand,
        aeName: initialData.aeName,
        category: initialData.category,
        typeOfPurchase: initialData.typeOfPurchase,
        slotForGP: '',
        valueBlockedByLineItem: initialData.exactBillingValue,
        impressionsBlockedOverContextualNode: 0,
        imSegment: '',
        valueToBeLockedAtPackageLevel: 0,
        impressions: 0,
        approxShareBlockedOverNode: 0,
    });

    const [selectedSlotPackage, setSelectedSlotPackage] = useState<string>('');
    const [availableSegments, setAvailableSegments] = useState<Option[]>([]);

    const [contextualNodes, setContextualNodes] = useState<
        IMPContextualNodeData[]
    >([
        {
            id: Date.now().toString(),
            imSegment: '',
            valueToBeLockedAtPackageLevel: 0,
            impressions: 0,
            approxShareBlockedOverNode: 0,
        },
    ]);

    const [fieldErrors, setFieldErrors] =
        useState<DetailedBookingIMPMainFormFieldErrors>({
            entryNumber: '',
            slotForGP: '',
            valueBlockedByLineItem: '',
            impressionsBlockedOverContextualNode: '',
        });

    const [contextualNodeErrors, setContextualNodeErrors] = useState<
        IMPContextualNodeErrors[]
    >([]);

    const [hasValidationBeenTriggered, setHasValidationBeenTriggered] =
        useState(false);

    const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
    const [errorNodeId, setErrorNodeId] = useState<string>('');
    const [errorType, setErrorType] = useState<IMPFormErrorType>(
        IMPFormErrorType.IMPRESSIONS
    );
    const [previousNodeValues, setPreviousNodeValues] = useState<PreviousNodesValues | null>(null);

    const validateForm = () => {
        const { mainFormErrors, contextualNodeErrors } =
            validateDetailedBookingIMPFields({ formData, contextualNodes, t });

        const hasMainFormErrors = Object.values(mainFormErrors).some(
            (error) => error !== ''
        );
        const hasContextualNodeErrors = contextualNodeErrors.some((nodeErrors) =>
            Object.values(nodeErrors).some((error) => error !== '')
        );

        if (hasValidationBeenTriggered) {
            setFieldErrors(mainFormErrors);
            setContextualNodeErrors(contextualNodeErrors);
        }

        return !hasMainFormErrors && !hasContextualNodeErrors;
    };

    useEffect(() => {
        if (entryId)
            setFormData((prev) => ({
                ...prev,
                entryNumber: entryId,
            }));
    }, [entryId]);

    useEffect(() => {
        if (existingContextualNodes && existingContextualNodes.length > 0) {
            const initialContextualNodes: IMPContextualNodeData[] =
                existingContextualNodes.map((slot, index) => ({
                    id: `existing-${index}`,
                    imSegment: slot.contextualNode || '',
                    valueToBeLockedAtPackageLevel: slot.valueBlockedAtPackageLevel || 0,
                    impressions: slot.impressionsBooked || 0,
                    approxShareBlockedOverNode: slot.percentageBooked || 0,
                }));

            if (existingContextualNodes[0]) {
                const firstSlot = existingContextualNodes[0];
                setFormData((prev) => ({
                    ...prev,
                    slotForGP: firstSlot.slotOrPackageName || '',
                    valueBlockedByLineItem: initialData.exactBillingValue,
                }));
                if (firstSlot.slotOrPackageName) setSelectedSlotPackage(firstSlot.slotOrPackageName);
            }

            setContextualNodes(initialContextualNodes);
        }
    }, [existingContextualNodes]);

    // Effect to handle slot package selection changes
    useEffect(() => {
        if (selectedSlotPackage && slotPackagesData) {
            const selectedPackage = slotPackagesData.find(
                (pkg) => pkg.slotPackageName === selectedSlotPackage
            );

            if (selectedPackage) {
                const segments = selectedPackage.segments
                    .filter(({ segmentName }) => {
                        // Check if this segment is NOT already used
                        return !contextualNodes.some(
                            ({ imSegment }) => imSegment === segmentName
                        );
                    })
                    .map(({ segmentName }) => ({
                        id: segmentName,
                        value: segmentName,
                        label: segmentName,
                    }));

                setAvailableSegments(segments);
            }
        }
    }, [selectedSlotPackage, slotPackagesData, contextualNodes]);

    useEffect(() => {
        setFormData((prev) => ({
            ...prev,
            valueBlockedByLineItem: effectiveRoValue,
        }));
    }, [effectiveRoValue]);

    // Calculate total impressions from all contextual nodes
    useEffect(() => {
        const totalImpressions = contextualNodes.reduce((total, node) => {
            const impressions = parseFloat(node.impressions.toString()) || 0;
            return total + impressions;
        }, 0);

        setFormData((prev) => ({
            ...prev,
            impressionsBlockedOverContextualNode: totalImpressions,
        }));

        const totalBudget = contextualNodes.reduce((total, node) => {
            return total + (parseFloat(node.valueToBeLockedAtPackageLevel.toString()) || 0);
        }, 0);
        onSlotBudgetChange?.(totalBudget);
    }, [contextualNodes]);

    useEffect(() => {
        if (validateFormTrigger) {
            setHasValidationBeenTriggered(true);
            const isValid = validateForm();
            if (isValid && onFormSubmit) onFormSubmit(formData, contextualNodes);
        }
    }, [validateFormTrigger]);

    const resetFieldErrors = () => {
        setFieldErrors({
            entryNumber: '',
            slotForGP: '',
            valueBlockedByLineItem: '',
            impressionsBlockedOverContextualNode: '',
        });
        setContextualNodeErrors([]);
        setHasValidationBeenTriggered(false);
    }

    useEffect(() => {
        if (resetDetailedFormErrors) {
            resetFieldErrors();
            setResetDetailedFormErrors?.(false);
        }
    }, [resetDetailedFormErrors]);

    useEffect(resetFieldErrors, []);

    const handleInputChange = (field: string, value: string) => {
        const numericFields: string[] = [
            FORM_FIELDS.VALUE_BLOCKED_BY_LINE_ITEM,
            FORM_FIELDS.IMPRESSIONS_BLOCKED_OVER_CONTEXTUAL_NODES,
        ];
        const processedValue = getProcessedValue(numericFields, field, value);

        if (field === DETAILED_BOOKING_IMP_FIELDS.SLOT_FOR_GP) {
            setFormData((prev) => ({
                ...prev,
                [field]: processedValue as string,
                valueBlockedByLineItem: effectiveRoValue,
            }));
            setSelectedSlotPackage(value);
            setContextualNodes([{
                id: Date.now().toString(),
                imSegment: '',
                valueToBeLockedAtPackageLevel: 0,
                impressions: 0,
                approxShareBlockedOverNode: 0,
            }]);
            onSlotSelectionChange(value);
            return;
        }

        setFormData((prev) => ({ ...prev, [field]: processedValue }));
    };

    useEffect(() => {
        if (hasValidationBeenTriggered) {
            validateForm();
        }
    }, [formData, contextualNodes, hasValidationBeenTriggered]);

    const isApprovedBooking = isEditMode;
    const existingContextualNodeValues = computeExistingContextualNodeValues(existingContextualNodes);

    const handleTransformedContextualNodeChange = (
        nodeId: string,
        field: string,
        value: string
    ) => {
        handleContextualNodeChange({
            nodeId,
            field,
            value,
            contextualNodes,
            setContextualNodes,
            formData,
            setErrorNodeId,
            setErrorType,
            setShowErrorModal,
            selectedSlotPackage,
            slotPackagesData,
            isEditMode,
            setPreviousNodeValues,
            isApprovedBooking,
            existingContextualNodeValues,
            isWaitlistEnabled,
        });
    };

    /* istanbul ignore next -- only reached when the error modal fires via handleContextualNodeChange (mocked in tests) */
    const getErrorModalTitleMessage = () => {
        if (errorType === IMPFormErrorType.BUDGET) {
            return {
                title: t('budget-limit-exceeded-title'),
                message: t('budget-limit-exceeded-message', {
                    roValue: formData.valueBlockedByLineItem,
                }),
            };
        }
        return {
            title: t('impressions-availability-error-title'),
            message: t('impressions-availability-error-message'),
        };
    };

    const errorModalProps = {
        showErrorModal,
        setShowErrorModal,
        errorNodeId,
        setErrorNodeId,
        setContextualNodes,
        isEditMode,
        previousNodeValues,
        setPreviousNodeValues,
        ...getErrorModalTitleMessage()
    };

    const impMainFormProps = {
        formData,
        handleInputChange,
        fieldErrors,
        hasValidationBeenTriggered,
        slotPackagesData,
        isLoadingSlotPackages: false,
        hideCommonFields,
        preSelectedSlotNames,
    };

    const partialContextualNodeFieldProps = {
        contextualNodes,
        contextualNodeErrors,
        hasValidationBeenTriggered,
        handleContextualNodeChange: handleTransformedContextualNodeChange,
        availableSegments,
        selectedSlotPackage,
        slotPackagesData,
    };

    const contextualNodeSectionProps = {
        contextualNodes,
        setContextualNodes,
        setContextualNodeErrors,
        partialContextualNodeFieldProps
    };

    // Pre-submit waitlist hint: compute the split when the waitlist is on and demand exceeds availability.
    // Requests are aggregated per segment first so multiple nodes on the same segment share one
    // availability pool (consistent with validateImpressionsAvailability's in-session accounting).
    const waitlistHintMessage = (() => {
        if (!isWaitlistEnabled || !selectedSlotPackage || !slotPackagesData.length) return '';
        const pkg = slotPackagesData.find((p) => p.slotPackageName === selectedSlotPackage);
        if (!pkg) return '';

        const segmentTotals = new Map<string, { requested: number; avail: number }>();
        for (const node of contextualNodes) {
            if (!node.imSegment) continue;
            const seg = pkg.segments.find((s) => s.segmentName === node.imSegment);
            if (!seg) continue;
            const avail = Math.max(0, Math.floor((seg.overAllImpressions * seg.availability) / 100) - seg.impressionsBooked);
            const entry = segmentTotals.get(node.imSegment) ?? { requested: 0, avail };
            entry.requested += parseFloat(node.impressions?.toString()) || 0;
            segmentTotals.set(node.imSegment, entry);
        }

        let totalWaitlisted = 0;
        let totalAvailable = 0;
        for (const { requested, avail } of segmentTotals.values()) {
            /* istanbul ignore next -- requires waitlist over-request state unreachable in renderHook tests */
            if (requested > avail) {
                totalWaitlisted += requested - avail;
                totalAvailable += avail;
            }
        }
        if (totalWaitlisted <= 0) return '';
        /* istanbul ignore next */
        return t('waitlist-hint-split', {
            available: totalAvailable.toLocaleString(),
            waitlisted: totalWaitlisted.toLocaleString(),
        });
    })();

    return {
        formData,
        fieldErrors,
        showErrorModal,
        errorModalProps,
        impMainFormProps,
        hasValidationBeenTriggered,
        contextualNodeSectionProps,
        waitlistHintMessage,
    }
}
